import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, RefreshCw, X } from 'lucide-react';
import { BUILD_TIME, VERSION } from '../../version';
import './WebsiteVersionDialog.css';

export default function WebsiteVersionDialog({
  onClose, onUpdate, outdated = false, available = false, automatic = false,
  updating = false, confirmUpdate = false, updateFailed = false, english = false,
}) {
  const panelRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const builtAt = BUILD_TIME && Number.isFinite(Date.parse(BUILD_TIME))
    ? new Intl.DateTimeFormat('zh-CN', {
      timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).format(new Date(BUILD_TIME)) : '—';
  useEffect(() => {
    const dismissOutside = event => {
      if (!panelRef.current?.contains(event.target)) onClose();
    };
    const dismissEscape = event => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', dismissOutside, true);
    document.addEventListener('keydown', dismissEscape);
    return () => {
      document.removeEventListener('pointerdown', dismissOutside, true);
      document.removeEventListener('keydown', dismissEscape);
    };
  }, [onClose]);

  useEffect(() => {
    if (paused || automatic || confirmUpdate || updating) return undefined;
    const timer = window.setTimeout(onClose, available || outdated ? 12000 : 8000);
    return () => window.clearTimeout(timer);
  }, [automatic, available, confirmUpdate, onClose, outdated, paused, updating]);

  const title = updating ? (english ? 'Updating Xiaoying' : '正在更新小影')
    : confirmUpdate ? (english ? 'Ready to refresh?' : '准备好更新了吗')
      : updateFailed ? (english ? 'Update not completed' : '暂未完成更新')
      : available ? (english ? 'An update is ready' : '小影有新版本了')
        : outdated ? (english ? 'Update not loaded yet' : '新版本暂未载入')
          : (english ? 'Xiaoying is up to date' : '小影已更新');
  const detail = updating ? (english ? 'Returning to this page shortly.' : '更新后会回到当前页面。')
    : confirmUpdate ? (english ? 'Save edits and unsent messages before refreshing.' : '请先保存编辑内容和未发送的消息，再刷新。')
      : updateFailed ? (english ? 'This page is still here. You can try again later.' : '当前页面仍保留，可以稍后重试。')
      : automatic ? (english ? 'This idle page will update shortly.' : '即将为你更新页面，点击其他位置可稍后再说。')
        : available ? (english ? 'Refresh when ready to try the latest version.' : '新版已就绪，方便时刷新体验。')
          : outdated ? (english ? 'You can keep working and refresh later.' : '可以继续使用，稍后刷新再试。')
            : (english ? 'The latest version is ready. Keep creating.' : '最新版本已就绪，继续创作吧。');

  return createPortal(
    <div className="website-version">
      <section className="website-version__panel" ref={panelRef}
        aria-label={english ? 'Website update' : '版本更新提示'}
        onPointerEnter={() => setPaused(true)}
        onPointerLeave={() => setPaused(panelRef.current?.contains(document.activeElement))}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={event => {
          if (!event.currentTarget.contains(event.relatedTarget)) setPaused(event.currentTarget.matches(':hover'));
        }}>
        <span className={`website-version__icon${available || outdated ? ' is-update' : ''}`} aria-hidden="true">
          {available || outdated || updating ? <RefreshCw size={16} /> : <Check size={17} />}
        </span>
        <div className="website-version__content" role="status" aria-live="polite" aria-atomic="true"
          title={`${VERSION} · ${builtAt}${english ? ' (Beijing time)' : '（北京时间）'}`}>
          <p className="website-version__title">{title}</p>
          <p className="website-version__detail">{detail}</p>
        </div>
        {available && <button type="button" className="website-version__update" onClick={onUpdate} disabled={updating}>
          {updating ? (english ? 'Updating…' : '更新中…')
            : confirmUpdate ? (english ? 'Saved, refresh' : '已保存，刷新')
              : (english ? 'Update now' : '立即更新')}
        </button>}
        <button type="button" className="website-version__dismiss" onClick={onClose} disabled={updating}
          aria-label={english ? 'Dismiss update notice' : '关闭更新提示'}>
          <X size={16} aria-hidden="true" />
        </button>
      </section>
    </div>, document.body,
  );
}
